import { DotLegalButton } from "@dotlegal/dotlegal-ui-components";
import { Box } from "@mui/material";
import React from "react";
import { useTranslation } from "@localization/useTranslation.ts";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider";
import { EntityType } from "@common/Common.types.ts";
import { isNullOrWhitespace } from "@common/stringOperations.ts";
import { DownloadAllDocumentsButton } from "@common/components/downloadAllDocuments/DownloadAllDocumentsButton";
import AddDocumentsEmptyState from "@common/components/documentsEmptyState/AddDocumentsEmptyState.tsx";
import { useIncidentDocumentPanes, useIncidentDocuments } from "./IncidentDocument.hooks";
import IncidentDocumentTable from "./IncidentDocumentTable.tsx";

export interface IncidentDocumentsProps {
    incidentId: string;
    incidentName?: string;
    // The incident's template step shows an empty incident as a grey box to click, like the contract documents field;
    // the overview tab sits in its own paper and keeps the table's empty state.
    emptyStateWrappedInPaper: boolean;
    errorText?: string;
}

function IncidentDocuments(props: IncidentDocumentsProps) {
    const { translateString } = useTranslation();
    const { permissions } = useUserContext();
    const { isLoading, data, hasDocuments, refetchDocuments } = useIncidentDocuments(props.incidentId);
    const { openAddDocumentPane, openEditDocumentPane } = useIncidentDocumentPanes(props.incidentId, refetchDocuments);
    const canAddDocument = permissions.incidentLogPermissions.edit && !isNullOrWhitespace(props.incidentId);

    return (
        <>
            {props.emptyStateWrappedInPaper && !hasDocuments ? (
                <AddDocumentsEmptyState canAdd={canAddDocument} onAddClick={openAddDocumentPane} errorText={props.errorText} />
            ) : (
                <IncidentDocumentTable
                    incidentId={props.incidentId}
                    incidentName={props.incidentName}
                    isLoading={isLoading}
                    documents={data}
                    refetchDocuments={() => refetchDocuments()}
                    onEditDocument={openEditDocumentPane}
                />
            )}
            <Box sx={{ display: "flex", justifyContent: "flex-end", marginTop: 2, gap: 2 }}>
                <DownloadAllDocumentsButton entityType={EntityType.IncidentLog} entityId={props.incidentId} show={(data?.length ?? 0) > 1} />
                {permissions.incidentLogPermissions.edit && (
                    <DotLegalButton buttonType={"primary"} disabled={!canAddDocument} onClick={openAddDocumentPane}>
                        {translateString("addDocument")}
                    </DotLegalButton>
                )}
            </Box>
        </>
    );
}

export default IncidentDocuments;
